import { Colors, Icon, NonIdealState } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { createSelector } from '@reduxjs/toolkit';
import classNames from 'classnames';
import * as React from 'react';
import { alphaSort, numericSort } from 'vet-bones/utils';

import { Link } from 'src/components/Link';
import {
  getProjectDiagramModelTableCellLinkTestId,
  getProjectDiagramModelTableModelDotTestId,
} from 'src/constants/testIds';
import { modelFieldsSelector } from 'src/containers/ProjectDiagram/components/modelList/modelFieldsSelector';
import { ProjectDiagramModelTable } from 'src/containers/ProjectDiagram/components/modelList/ProjectDiagramModelTable';
import {
  AttributeTableItem,
  TableField,
} from 'src/containers/ProjectDiagram/components/modelList/types';
import { useClickModelConceptAttributeLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptAttributeLink';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { memoizeSelectorFactory } from 'src/selectors/memoizeSelectorFactory';
import { projectAttributesByIriSelector } from 'src/selectors/projectAttributesByIri';
import { projectHiddenModelsSelector } from 'src/selectors/projectHiddenModels';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectModelIdsByIriSelector } from 'src/selectors/projectModelsIdsByIri';
import { ModelListTab, ModelListTableData } from 'src/store/ui/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const INITIAL_FIELDS: Array<TableField<AttributeTableItem>> = [
  {
    id: 'name',
    label: copy.components.projectDiagram.modelList.attributes.name,
    sorter: (direction: 'asc' | 'desc') =>
      alphaSort<AttributeTableItem>(
        (item) => item.name.value,
        direction === 'desc'
      ),
    sortIconAsc: IconNames.SORT_ALPHABETICAL,
    sortIconDesc: IconNames.SORT_ALPHABETICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortTextAsc,
    sortLabelDesc: copy.components.projectDiagram.modelList.table.sortTextDesc,
    value: 'name',
  },
  {
    cellClassName: 'sd-project-diagram-model-table-cell--monospace',
    hidden: true,
    id: 'iri',
    label: copy.components.projectDiagram.modelList.attributes.iri,
    sorter: (direction: 'asc' | 'desc') =>
      alphaSort<AttributeTableItem>(
        (item) => item.iri.value,
        direction === 'desc'
      ),
    sortIconAsc: IconNames.SORT_ALPHABETICAL,
    sortIconDesc: IconNames.SORT_ALPHABETICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortTextAsc,
    sortLabelDesc: copy.components.projectDiagram.modelList.table.sortTextDesc,
    value: 'iri',
  },
  {
    id: 'ranges',
    label: copy.components.projectDiagram.modelList.attributes.ranges,
    sorter: (direction: 'asc' | 'desc') =>
      alphaSort<AttributeTableItem>(
        (item) => item.ranges.value.map((range) => range.label).join(', '),
        direction === 'desc'
      ),
    sortIconAsc: IconNames.SORT_ALPHABETICAL,
    sortIconDesc: IconNames.SORT_ALPHABETICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortTextAsc,
    sortLabelDesc: copy.components.projectDiagram.modelList.table.sortTextDesc,
    value: 'ranges',
  },
  {
    id: 'domains',
    label: copy.components.projectDiagram.modelList.attributes.domains,
    sorter: (direction: 'asc' | 'desc') =>
      alphaSort<AttributeTableItem>(
        (item) => item.domains.value.map((domain) => domain.label).join(', '),
        direction === 'desc'
      ),
    sortIconAsc: IconNames.SORT_ALPHABETICAL,
    sortIconDesc: IconNames.SORT_ALPHABETICAL_DESC,
    sortLabelAsc: copy.components.projectDiagram.modelList.table.sortTextAsc,
    sortLabelDesc: copy.components.projectDiagram.modelList.table.sortTextDesc,
    value: 'domains',
  },
];

const attributeTableItemsSelector = memoizeSelectorFactory(
  (projectId: string) =>
    createSelector(
      entityDrawerSelector,
      projectLabelsByIriSelector(projectId),
      projectAttributesByIriSelector(projectId),
      projectModelIdsByIriSelector(projectId),
      projectHiddenModelsSelector(projectId),
      modelFieldsSelector<AttributeTableItem>(projectId),
      (
        entityDrawer,
        labelsByIri,
        attributesByIri,
        modelIdsByIri,
        hiddenModelIds,
        modelFields
      ) =>
        Object.values(attributesByIri)
          .map(({ value: attributeIri, domains, ranges }) => {
            return {
              hidden: Boolean(
                modelIdsByIri[attributeIri]?.every((model) =>
                  hiddenModelIds?.includes(model)
                )
              ),
              id: attributeIri,
              name: {
                label: null,
                selected: entityDrawer.entityValue === attributeIri,
                value: getLabelForIri(
                  labelsByIri,
                  attributeIri,
                  copy.components.projectDiagram.modelConceptAttribute.new
                ),
              },
              iri: {
                label: null,
                value: attributeIri,
              },
              domains: {
                label: null,
                value: Object.values(domains)
                  .map(({ value: domain }) => ({
                    hidden: Boolean(
                      modelIdsByIri[domain]?.every((model) =>
                        hiddenModelIds?.includes(model)
                      )
                    ),
                    id: domain,
                    label: getLabelForIri(
                      labelsByIri,
                      domain,
                      copy.components.projectDiagram.modelConceptClass.new
                    ),
                  }))
                  .sort(alphaSort((domain) => domain.label)),
              },
              ranges: {
                label: null,
                selected:
                  entityDrawer.entityValue && ranges[entityDrawer.entityValue],
                value: Object.values(ranges)
                  .map(({ value: range }) => ({
                    id: range,
                    label: getLabelForIri(labelsByIri, range),
                  }))
                  .sort(alphaSort((range) => range.label)),
              },
              ...Object.fromEntries(
                modelFields.map((field) => [
                  field.value,
                  {
                    label: null,
                    value: {
                      checked: modelIdsByIri[attributeIri]?.includes(
                        field.value
                      ),
                      hidden:
                        !field.value || hiddenModelIds?.includes(field.value),
                    },
                  },
                ])
              ),
            } as AttributeTableItem;
          })
          .sort(alphaSort((item) => item.name.value))
    )
);

export const ProjectDiagramModelAttributes: React.VFC = () => {
  const dispatch = useReduxDispatch();

  const { projectId } = useRouterParams();
  // modelFieldsSelector is generic, so it can't be memoized at the source;
  // memoize the instance here instead.
  const modelFieldsSelectorInstance = React.useMemo(
    () => modelFieldsSelector<AttributeTableItem>(projectId),
    [projectId]
  );
  const modelFields = useReduxSelector(modelFieldsSelectorInstance);
  const attributeItems = useReduxSelector(
    attributeTableItemsSelector(projectId)
  );

  const handleClickClass = useClickModelConceptClassLink();
  const handleClickAttribute = useClickModelConceptAttributeLink();

  const tableData = useReduxSelector(
    (state) => state.ui.modelList.tableData[ModelListTab.ATTRIBUTES]
  );
  const handleUpdateTableData = (
    data: ModelListTableData<AttributeTableItem>
  ) => {
    dispatch(
      uiSlice.actions.updatedModelListTableData({
        tab: ModelListTab.ATTRIBUTES,
        data,
      })
    );
  };

  const attributeFields = React.useMemo(
    () =>
      INITIAL_FIELDS.concat(
        modelFields.map((field) => ({
          ...field,
          sorter: (direction: 'asc' | 'desc') =>
            numericSort<AttributeTableItem>(
              (item) => (item[field.value]?.value.checked ? 1 : -1),
              direction === 'desc'
            ),
        }))
      ),
    [modelFields]
  );

  const attributeItemsWithLinks = React.useMemo(
    () =>
      attributeItems.map(
        ({ hidden, id, name, iri, domains, ranges, ...models }, idx) =>
          ({
            hidden,
            id,
            name: {
              label: (
                <>
                  <Link
                    className={classNames(
                      'sd-project-diagram-model-table-link',
                      { 'sd-project-diagram-model-table-link--weak': hidden }
                    )}
                    onClick={() => {
                      handleClickAttribute({
                        text: name.value,
                        value: id,
                      });
                    }}
                    testId={getProjectDiagramModelTableCellLinkTestId(id, idx)}
                  >
                    {name.value}
                  </Link>
                </>
              ),
              selected: name.selected,
              value: name.value,
            },
            iri: {
              label: iri.value,
              value: iri.value,
            },
            domains: {
              label: (
                <>
                  {domains.value.map((domain, domainIdx) => (
                    <React.Fragment key={domain.id}>
                      {domainIdx ? ', ' : ''}
                      <Link
                        className={classNames(
                          'sd-project-diagram-model-table-link',
                          {
                            'sd-project-diagram-model-table-link--weak':
                              domain.hidden,
                          }
                        )}
                        onClick={() => {
                          handleClickClass({
                            text: domain.label,
                            value: domain.id,
                          });
                        }}
                      >
                        {domain.label}
                      </Link>
                    </React.Fragment>
                  ))}
                </>
              ),
              selected: domains.selected,
              value: domains.value,
            },
            ranges: {
              label: (
                <>
                  {ranges.value.map((range, rangeIdx) => (
                    <React.Fragment key={range.id}>
                      {rangeIdx ? ', ' : ''}
                      {range.label}
                    </React.Fragment>
                  ))}
                </>
              ),
              value: ranges.value,
            },
            ...Object.fromEntries(
              Object.entries(models).map(([modelId, model], modelIdx) => [
                modelId,
                {
                  label: (
                    <Icon
                      // Grey alone can't separate a hidden model's dot from a
                      // normal one, so hidden also takes the weak treatment the
                      // greyed names use.
                      className={classNames({
                        'sd-project-diagram-model-table-link--weak':
                          model.value.hidden,
                      })}
                      color={
                        !model.value.checked || model.value.hidden
                          ? Colors.GRAY4
                          : undefined
                      }
                      data-testid={getProjectDiagramModelTableModelDotTestId(
                        modelId,
                        modelIdx
                      )}
                      icon={
                        model.value.checked
                          ? IconNames.FULL_CIRCLE
                          : IconNames.CIRCLE
                      }
                    />
                  ),
                  value: model.value,
                },
              ])
            ),
          } as AttributeTableItem)
      ),
    [attributeItems, handleClickClass, handleClickAttribute]
  );

  if (!attributeItemsWithLinks.length) {
    return (
      <NonIdealState
        description={
          copy.components.projectDiagram.modelList.attributes.emptyState
            .description
        }
        title={
          copy.components.projectDiagram.modelList.attributes.emptyState.title
        }
      />
    );
  }

  return (
    <ProjectDiagramModelTable<AttributeTableItem>
      fields={attributeFields}
      items={attributeItemsWithLinks}
      onUpdateTableData={handleUpdateTableData}
      tableData={tableData}
    />
  );
};
